/**
 * BC PharmaCare deductible / family-maximum estimator.
 *
 * This is intentionally a *patient-facing estimate*, not a billing
 * adjudication. Real claim adjudication happens at the dispense — the
 * pharmacy submits to PharmaNet with PharmaCare details and the
 * remittance comes back. What we provide here is a "this is roughly
 * what you'll pay out of pocket" hint that helps customers decide
 * whether to fill at our pharmacy.
 *
 * Reference: BC Ministry of Health PharmaCare Plans
 *   https://www2.gov.bc.ca/gov/content/health/practitioner-professional-resources/pharmacare/pharmacare-publications/bc-pharmacare-formulary
 *
 * The numbers below are PharmaCare Plan B/C/F/G/P/W rules as of the
 * 2025/26 fiscal year. They are *thresholds and percentages*, not the
 * Low Cost Alternative (LCA) prices, so they don't go stale every time
 * a generic price changes. Confirm against the published guide before
 * shipping; helpful comments inline.
 */

export type PharmaCarePlan =
  | "fair-pharmacare" // Plan I — income-based
  | "plan-c" // Income Assistance recipients
  | "plan-b" // Long-term care
  | "plan-f" // Children in the At-Home Program
  | "plan-g" // Mental-health-no-charge
  | "plan-p" // Palliative care
  | "plan-w" // First Nations Health Benefits
  | "none";

/**
 * Income brackets used by Fair PharmaCare. Net income in CAD$.
 * Source: PharmaCare guide — figures rounded to the published values
 * for the 2025/26 plan year. Adjust annually in February.
 */
export const FAIR_PHARMACARE_BRACKETS: Array<{
  maxNetIncome: number; // upper bound, inclusive
  deductiblePct: number; // % of net income paid before any coverage
  familyMaxPct: number; // % of net income at which 100% kicks in
  copayAfterDeductible: number; // % the patient pays after deductible
}> = [
  // Below $15,000: no deductible, no family max, 0% co-pay (Plan covers 100%)
  { maxNetIncome: 15_000, deductiblePct: 0, familyMaxPct: 0, copayAfterDeductible: 0 },
  // $15,001-$30,000: 1% deductible, 2% family max, 30% co-pay
  { maxNetIncome: 30_000, deductiblePct: 0.01, familyMaxPct: 0.02, copayAfterDeductible: 0.30 },
  // $30,001+ (pre-1939 / standard split): 2% deductible, 3% family max, 30% co-pay
  { maxNetIncome: Infinity, deductiblePct: 0.02, familyMaxPct: 0.03, copayAfterDeductible: 0.30 },
];

export type EstimateInput = {
  plan: PharmaCarePlan;
  /** Drug retail price for the fill (CAD$). */
  drugPrice: number;
  /** Whether the dispensed drug is a Low Cost Alternative or has reference-based-pricing-equivalent generic. */
  isLcaGeneric: boolean;
  /** Annual net family income for Fair PharmaCare (CAD$). */
  netFamilyIncome?: number;
  /** Year-to-date PharmaCare-eligible drug spend (CAD$). */
  ytdEligibleSpend?: number;
};

export type EstimateResult = {
  /** Estimated patient out-of-pocket for this fill, CAD$. */
  patientPaysCad: number;
  /** Estimated PharmaCare contribution for this fill, CAD$. */
  pharmaCarePaysCad: number;
  /** Annual deductible (Fair PharmaCare). */
  annualDeductibleCad: number;
  /** Annual family max — once reached, PharmaCare covers 100%. */
  annualFamilyMaxCad: number;
  /**
   * Plain-English explanation of how we got there. Surface this in the
   * UI so users understand the estimate isn't a guarantee.
   */
  explanation: string;
};

/**
 * Compute an estimate. Always returns a result — the explanation
 * captures any "we couldn't model this" caveats. We never throw.
 */
export function estimate(input: EstimateInput): EstimateResult {
  const drugPrice = roundCents(Math.max(0, Number(input.drugPrice) || 0));
  const ytd = roundCents(Math.max(0, Number(input.ytdEligibleSpend) || 0));

  // Plans that cover 100% with no patient cost share.
  if (
    input.plan === "plan-c" ||
    input.plan === "plan-b" ||
    input.plan === "plan-g" ||
    input.plan === "plan-p" ||
    input.plan === "plan-w"
  ) {
    return {
      patientPaysCad: 0,
      pharmaCarePaysCad: drugPrice,
      annualDeductibleCad: 0,
      annualFamilyMaxCad: 0,
      explanation: explainFullCover(input.plan),
    };
  }

  if (input.plan === "plan-f") {
    return {
      patientPaysCad: 0,
      pharmaCarePaysCad: drugPrice,
      annualDeductibleCad: 0,
      annualFamilyMaxCad: 0,
      explanation:
        "Plan F (Children in the At-Home Program) typically covers eligible " +
        "drugs at 100% with no patient cost share.",
    };
  }

  if (input.plan === "fair-pharmacare") {
    const income = Math.max(0, Number(input.netFamilyIncome) || 0);
    if (income === 0) {
      return {
        patientPaysCad: drugPrice,
        pharmaCarePaysCad: 0,
        annualDeductibleCad: 0,
        annualFamilyMaxCad: 0,
        explanation:
          "We need your net family income to estimate Fair PharmaCare " +
          "coverage. Without it we have to assume no coverage.",
      };
    }

    const bracket = FAIR_PHARMACARE_BRACKETS.find((b) => income <= b.maxNetIncome)
      ?? FAIR_PHARMACARE_BRACKETS[FAIR_PHARMACARE_BRACKETS.length - 1];

    const deductible = roundCents(income * bracket.deductiblePct);
    const familyMax = roundCents(income * bracket.familyMaxPct);
    const remainingDeductible = Math.max(0, deductible - ytd);
    const remainingToMax = Math.max(0, familyMax - ytd);

    let patient = 0;
    let plan = 0;

    // Step 1: pay against the deductible at 100%.
    const towardDeductible = Math.min(drugPrice, remainingDeductible);
    patient += towardDeductible;

    // Step 2: any remainder past the deductible but below the family max
    // is split copayAfterDeductible (typically 30%) patient / 70% plan.
    let remainder = drugPrice - towardDeductible;
    const towardFamilyMax = Math.min(remainder, Math.max(0, remainingToMax - towardDeductible));
    patient += roundCents(towardFamilyMax * bracket.copayAfterDeductible);
    plan += roundCents(towardFamilyMax * (1 - bracket.copayAfterDeductible));

    remainder -= towardFamilyMax;

    // Step 3: anything past the family max is fully covered.
    if (remainder > 0) plan += remainder;

    let explanation = `Fair PharmaCare estimate using ` +
      `$${income.toLocaleString()} net family income. ` +
      `Annual deductible: $${deductible.toFixed(2)}. ` +
      `Family maximum (then 100% covered): $${familyMax.toFixed(2)}. `;

    if (remainingDeductible > 0) {
      explanation += `You haven't reached your deductible yet — ` +
        `the first $${remainingDeductible.toFixed(2)} of eligible drug ` +
        `spend this year is on you.`;
    } else if (remainingToMax > 0) {
      explanation += `You're past your deductible. PharmaCare pays ` +
        `${Math.round((1 - bracket.copayAfterDeductible) * 100)}% of ` +
        `eligible drug costs until you hit your family maximum.`;
    } else {
      explanation += `You've reached your family maximum for the year — ` +
        `eligible drugs are now 100% covered.`;
    }

    if (!input.isLcaGeneric) {
      explanation += " Note: brand-name drugs above the Low Cost " +
        "Alternative price may have a reference-pricing gap that you " +
        "pay on top of this estimate.";
    }

    return {
      patientPaysCad: roundCents(patient),
      pharmaCarePaysCad: roundCents(plan),
      annualDeductibleCad: deductible,
      annualFamilyMaxCad: familyMax,
      explanation,
    };
  }

  // No plan / private only: customer pays full retail.
  return {
    patientPaysCad: drugPrice,
    pharmaCarePaysCad: 0,
    annualDeductibleCad: 0,
    annualFamilyMaxCad: 0,
    explanation:
      "No PharmaCare plan selected — this estimate assumes you pay the " +
      "retail price. If you have private insurance, your insurer will " +
      "reimburse a portion separately.",
  };
}

function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

function explainFullCover(plan: PharmaCarePlan): string {
  switch (plan) {
    case "plan-c":
      return "Plan C (Income Assistance) covers eligible drugs at 100% with no deductible.";
    case "plan-b":
      return "Plan B (Long-Term Care) covers eligible drugs at 100% for residents of licensed facilities.";
    case "plan-g":
      return "Plan G (Mental Health) covers eligible psychiatric medications at 100% with prescriber-confirmed eligibility.";
    case "plan-p":
      return "Plan P (Palliative Care) covers eligible drugs at 100% once enrolled.";
    case "plan-w":
      return "First Nations Health Benefits covers eligible drugs at 100% (Plan W).";
    default:
      return "This plan typically covers eligible drugs at 100%.";
  }
}
